/**
 * wsh-bundle-cart-add.js — one shared add-to-cart for bundles (PP-1 S5, decision 37).
 *
 * The build-your-own band's add (blocks/wsh-bundle-card.liquid), moved here as it was, so the band and the
 * "Your sound" page call the same steps:
 *   1. read /cart.js and skip variant IDs already there (a failed read counts as an empty cart);
 *   2. one shared 8-character _bundle_token on every line it adds, plus _bundle_source (and _bundle_page when given);
 *   3. POST /cart/add.js with {items, sections} (the page's cart-items-component section IDs);
 *   4. cart:update with the returned sections, then cart-drawer-component.showDialog() after 300 ms.
 *
 * API:
 *   window.wshBundleCartAdd({ variantIds, source, page, parent, plain, eventSource, timeoutMs, drawerOnAnyReply })
 *     parent  : { title, url, image } → _bundle_parent_title / _url / _image on every line, as the curated
 *               bundle product page sends them (app wsh-buy-box.js #addPreconfiguredBundleToCart; decision 39).
 *     plain   : true → plain lines, no properties at all (the Discovery pack, as its product page; decision 40).
 *     → Promise<{ ok, added: [variantId], skipped: [{ variantId, title }], reason }>
 *     ok      : the add answered 2xx with no `status` error field (or there was nothing to add).
 *     reason  : 'nothing_to_add' | 'http_<code>' | 'network' | 'timeout' | 'cart_error' | null
 *     The drawer opens only when ok and something was added. With drawerOnAnyReply (the band, as before
 *     this slice) it also opens on any parsed reply, a failed add included.
 *   window.wshBundleCartAdd.openDrawer()  opens the cart drawer (no cart:update).
 *   Ready signal: document event 'wsh:bundle-cart-add-ready' once the function exists.
 *
 * Prices are never read here. Standing Rule 1.
 */
(function () {
  'use strict';
  if (window.wshBundleCartAdd) return;

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && drawer.showDialog) drawer.showDialog();
  }

  function announce(data, source, count) {
    var evt = new Event('cart:update', { bubbles: true });
    evt.detail = {
      resource: data,
      sourceId: source,
      data: { source: source, itemCount: count, sections: (data && data.sections) || {} }
    };
    document.dispatchEvent(evt);
    /* Wait for the drawer's DOM morph before opening it (as the band always has). */
    setTimeout(openDrawer, 300);
  }

  function add(opts) {
    opts = opts || {};
    var ids = (opts.variantIds || []).map(String).filter(Boolean);
    var eventSource = opts.eventSource || 'wsh-bundle-card';

    return fetch('/cart.js')
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (ci) {
          inCart[String(ci.variant_id)] = ci.product_title || ci.title || '';
        });
        var skipped = ids.filter(function (id) { return inCart.hasOwnProperty(id); })
          .map(function (id) { return { variantId: id, title: inCart[id] }; });
        var toAdd = ids.filter(function (id) { return !inCart.hasOwnProperty(id); });
        if (!toAdd.length) return { ok: true, added: [], skipped: skipped, reason: 'nothing_to_add' };

        var token = Math.random().toString(36).substring(2, 10);
        var items = toAdd.map(function (id) {
          if (opts.plain) return { id: Number(id), quantity: 1 };
          var props = { '_bundle_token': token };
          if (opts.parent) {
            props['_bundle_parent_title'] = opts.parent.title;
            props['_bundle_parent_url'] = opts.parent.url;
            props['_bundle_parent_image'] = opts.parent.image;
          }
          if (opts.source) props['_bundle_source'] = opts.source;
          if (opts.page) props['_bundle_page'] = opts.page;
          return { id: Number(id), quantity: 1, properties: props };
        });
        var secs = sectionIds();
        var body = { items: items };
        if (secs.length) body.sections = secs.join(',');

        var ctrl = (opts.timeoutMs && typeof AbortController === 'function') ? new AbortController() : null;
        var timedOut = false;
        var timer = ctrl ? setTimeout(function () { timedOut = true; ctrl.abort(); }, opts.timeoutMs) : null;
        var init = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
        if (ctrl) init.signal = ctrl.signal;

        return fetch('/cart/add.js', init)
          .then(function (r) {
            return r.json().catch(function () { return null; }).then(function (data) {
              if (timer) clearTimeout(timer);
              var ok = r.ok && !!data && data.status == null;
              if (ok || (opts.drawerOnAnyReply && data)) announce(data, eventSource, items.length);
              return { ok: ok, added: ok ? toAdd : [], skipped: skipped, reason: ok ? null : 'http_' + r.status };
            });
          }, function () {
            if (timer) clearTimeout(timer);
            return { ok: false, added: [], skipped: skipped, reason: timedOut ? 'timeout' : 'network' };
          });
      });
  }

  add.openDrawer = openDrawer;
  window.wshBundleCartAdd = add;
  document.dispatchEvent(new Event('wsh:bundle-cart-add-ready'));
})();
